import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaAppleAlt, FaSearch, FaFilter, FaEdit, FaTrash, FaEye,
  FaCalendarAlt, FaUserMd, FaUsers, FaFileMedical, FaPlus,
  FaTimes, FaCheckCircle, FaChevronLeft, FaChevronRight,
  FaRedo, FaDownload, FaSort, FaSortUp, FaSortDown,
  FaExclamationTriangle, FaInfoCircle, FaClock, FaTag
} from 'react-icons/fa';
import { GiForkKnifeSpoon } from 'react-icons/gi';

// Firebase
import { database } from '../firebase';
import { ref, onValue, remove } from 'firebase/database';

const AdminViewDiet = () => {
  const navigate = useNavigate();

  const [dietPlans, setDietPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [durationFilter, setDurationFilter] = useState('All');
  const [dietTypeFilter, setDietTypeFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [viewDetailsModal, setViewDetailsModal] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const durations = ['All', '7', '14', '21', '30', '45', '60', '90'];
  const dietTypes = ['All', 'Vegetarian', 'Non-Vegetarian', 'Both'];

  const [stats, setStats] = useState({
  total: 0,
  vegetarian: 0,
  nonVegetarian: 0,
  mixed: 0
});

  // Fetch real-time data from Firebase
  useEffect(() => {
    const dietPlansRef = ref(database, 'Dr/dietPlans');

    const unsubscribe = onValue(dietPlansRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const plansList = Object.values(data).map(plan => ({
          ...plan,
          id: plan.planId // Use numeric planId as unique identifier
        }));
        setDietPlans(plansList);
        setFilteredPlans(plansList);
        calculateStats(plansList);
      } else {
        setDietPlans([]);
        setFilteredPlans([]);
        setStats({ total: 0, vegetarian: 0, nonVegetarian: 0, mixed: 0 });
      }
      setLoading(false);
    }, (error) => {
      console.error('Error fetching diet plans:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter & sort when dependencies change
  useEffect(() => {
    let filtered = [...dietPlans];

    // Search
    if (searchQuery) {
      filtered = filtered.filter(plan =>
        plan.diseaseName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plan.doctorName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plan.ageGroup?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Duration filter
    if (durationFilter !== 'All') {
      const dur = parseInt(durationFilter);
      filtered = filtered.filter(plan => plan.duration === dur);
    }

    // Diet Type filter
    if (dietTypeFilter !== 'All') {
      filtered = filtered.filter(plan => plan.dietType === dietTypeFilter);
    }

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'duration') {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }

    setFilteredPlans(filtered);
    calculateStats(filtered);
  }, [searchQuery, durationFilter, dietTypeFilter, dietPlans, sortConfig]);

  const calculateStats = (plans) => {
    const total = plans.length;
    const vegetarian = plans.filter(p => p.dietType === 'Vegetarian').length;
    const nonVegetarian = plans.filter(p => p.dietType === 'Non-Vegetarian').length;
    const mixed = plans.filter(p => p.dietType === 'Both').length;
    setStats({ total, vegetarian, nonVegetarian, mixed });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDelete = async (planId) => {
    try {
      const planRef = ref(database, `Dr/dietPlans/${planId}`);
      await remove(planRef);
      setSuccessMessage('Diet plan deleted successfully!');
      setDeleteConfirmation(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete diet plan');
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-gray-400 ml-1" />;
    return sortConfig.direction === 'ascending' ? <FaSortUp className="text-teal-600 ml-1" /> : <FaSortDown className="text-teal-600 ml-1" />;
  };

  const getDietTypeColor = (type) => {
    switch (type) {
      case 'Vegetarian': return 'bg-green-100 text-green-800';
      case 'Non-Vegetarian': return 'bg-red-100 text-red-800';
      case 'Both': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPlans = filteredPlans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlans.length / itemsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading diet plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage Diet Plans</h1>
          <p className="text-gray-600 mt-2">View and manage all diet plans ({stats.total} total)</p>
          <div className="mt-6">
            <Link
              to="/admin/diet-plans/add"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:shadow-lg font-medium"
            >
              <FaPlus className="mr-2" /> Add New Diet Plan
            </Link>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center">
            <FaCheckCircle className="text-green-600 text-xl mr-3" />
            <p className="font-medium text-green-800">{successMessage}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow">
            <p className="text-gray-600">Total Plans</p>
            <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <p className="text-gray-600">Vegetarian</p>
            <p className="text-3xl font-bold text-green-600">{stats.vegetarian}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <p className="text-gray-600">Non-Vegetarian</p>
            <p className="text-3xl font-bold text-red-600">{stats.nonVegetarian}</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow">
            <p className="text-gray-600">Mixed</p>
            <p className="text-3xl font-bold text-blue-600">{stats.mixed}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Disease, doctor, age..."
                  className="pl-10 w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
              <select
                value={durationFilter}
                onChange={(e) => setDurationFilter(e.target.value)}
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
              >
                {durations.map(d => <option key={d} value={d}>{d === 'All' ? 'All' : `${d} Days`}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Diet Type</label>
              <select
                value={dietTypeFilter}
                onChange={(e) => setDietTypeFilter(e.target.value)}
                className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-teal-500"
              >
                {dietTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('diseaseName')}>
                    Disease Name {getSortIcon('diseaseName')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('duration')}>
                    Duration {getSortIcon('duration')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('dietType')}>
                    Diet Type {getSortIcon('dietType')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Doctor</th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Age Group</th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700 cursor-pointer" onClick={() => handleSort('createdAt')}>
                    Created {getSortIcon('createdAt')}
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPlans.length > 0 ? (
                  currentPlans.map((plan) => (
                    <tr key={plan.planId} className="hover:bg-teal-50/30">
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-3">
                          <FaFileMedical className="text-teal-600 text-xl" />
                          <div>
                            <p className="font-medium">{plan.diseaseName}</p>
                            <p className="text-sm text-gray-500">Code: {plan.planCode}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-6">{plan.duration} days</td>
                      <td className="py-5 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDietTypeColor(plan.dietType)}`}>
                          {plan.dietType}
                        </span>
                      </td>
                      <td className="py-5 px-6">{plan.doctorName}</td>
                      <td className="py-5 px-6">{plan.ageGroup}</td>
                      <td className="py-5 px-6">{formatDate(plan.createdAt)}</td>
                      <td className="py-5 px-6">
                        <div className="flex gap-2">
                          <button onClick={() => setViewDetailsModal(plan)}
                            className="p-2 bg-teal-100 text-teal-600 rounded-lg hover:bg-teal-200">
                            <FaEye />
                          </button>
                          <button onClick={() => navigate(`/admin/diet-plans/edit/${plan.planId}`)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200">
                            <FaEdit />
                          </button>
                          <button onClick={() => setDeleteConfirmation(plan)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-12">
                      <GiForkKnifeSpoon className="text-6xl text-gray-300 mx-auto mb-4" />
                      <p className="text-xl text-gray-500">No diet plans found</p>
                      <p className="text-gray-400 mt-2">Start by adding a new diet plan!</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredPlans.length > itemsPerPage && (
            <div className="p-6 border-t flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPlans.length)} of {filteredPlans.length}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border rounded-lg disabled:opacity-50">
                  <FaChevronLeft />
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                  <button key={i} onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg ${currentPage === i + 1 ? 'bg-teal-600 text-white' : 'border hover:bg-gray-100'}`}>
                    {i + 1}
                  </button>
                ))}
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border rounded-lg disabled:opacity-50">
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Delete Diet Plan?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the plan for <strong>{deleteConfirmation.diseaseName}</strong>?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setDeleteConfirmation(null)}
                className="flex-1 py-3 border rounded-xl hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirmation.planId)}
                className="flex-1 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewDetailsModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{viewDetailsModal.diseaseName}</h2>
                <p className="text-gray-600">Plan Code: {viewDetailsModal.planCode}</p>
              </div>
              <button onClick={() => setViewDetailsModal(null)}
                className="p-3 hover:bg-gray-100 rounded-full">
                <FaTimes className="text-xl" />
              </button>
            </div>

            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div className="p-5 bg-teal-50 rounded-xl">
                  <h4 className="font-bold mb-3">Basic Information</h4>
                  <div className="space-y-2">
                    <p><strong>Duration:</strong> {viewDetailsModal.duration} days</p>
                    <p><strong>Diet Type:</strong> {viewDetailsModal.dietType}</p>
                    <p><strong>Age Group:</strong> {viewDetailsModal.ageGroup}</p>
                    <p><strong>Doctor:</strong> {viewDetailsModal.doctorName}</p>
                    <p><strong>Created:</strong> {formatDate(viewDetailsModal.createdAt)}</p>
                  </div>
                </div>

                <div className="p-5 bg-green-50 rounded-xl">
                  <h4 className="font-bold mb-3">Foods Allowed ✅</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.foodsAllowed.map((food, i) => (
                      <li key={i} className="text-gray-700">{food}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 bg-red-50 rounded-xl">
                  <h4 className="font-bold mb-3">Foods to Avoid ⚠️</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.foodsToAvoid.map((food, i) => (
                      <li key={i} className="text-gray-700">{food}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="p-5 bg-amber-50 rounded-xl">
                  <h4 className="font-bold mb-3">Breakfast 🌅</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.breakfast.map((item, i) => (
                      <li key={i} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 bg-orange-50 rounded-xl">
                  <h4 className="font-bold mb-3">Lunch 🍱</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.lunch.map((item, i) => (
                      <li key={i} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 bg-gray-50 rounded-xl">
                  <h4 className="font-bold mb-3">Snacks ☕</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.snacks.map((item, i) => (
                      <li key={i} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-5 bg-indigo-50 rounded-xl">
                  <h4 className="font-bold mb-3">Dinner 🌙</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    {viewDetailsModal.dinner.map((item, i) => (
                      <li key={i} className="text-gray-700">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 border-t">
              <h4 className="font-bold mb-3">Description</h4>
              <p className="text-gray-700">{viewDetailsModal.description}</p>
            </div>

            {viewDetailsModal.notes?.length > 0 && (
              <div className="p-6 border-t">
                <h4 className="font-bold mb-3">Additional Notes</h4>
                <ul className="list-disc pl-5 space-y-2">
                  {viewDetailsModal.notes.map((note, i) => (
                    <li key={i} className="text-gray-700">{note}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="p-6 border-t flex justify-end">
              <button onClick={() => setViewDetailsModal(null)}
                className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminViewDiet;